import React, { useEffect, useRef, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { useTranslation } from "react-i18next"
import { cn } from "../../../../lib"
import SearchSuggestionsSection from "./SearchSuggestionsSection"
import { X } from "lucide-react"
import CityInput from "./CityInput"

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640)
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640)
    window.addEventListener("resize", handler)
    return () => window.removeEventListener("resize", handler)
  }, [])
  return isMobile
}

const SearchSuggestionsModal = ({
  ref,
  activeField,
  destinations = [],
  isLoading = false,
  onQueryChange,
  onClearTerminals,
  onSelect,
  onClose,
  selectedFrom,
  selectedTo,
  initialQuery = "",
}) => {
  const { t } = useTranslation()
  const inputRef = useRef(null)
  const containerRef = useRef(null)
  const terminalsRef = useRef(null)
  const [query, setQuery] = useState(initialQuery)

  const isMobile = useIsMobile()
  const isDeparture = activeField === "from"
  const selected = isDeparture ? selectedFrom : selectedTo
  const terminals = selected ? [selected] : []

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Блокируем скролл body на мобиле
  useEffect(() => {
    if (!isMobile) return
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = ""
    }
  }, [isMobile])

  const calculateOffset = () => {
    if (!containerRef.current || !terminalsRef.current) return
    const containerWidth = containerRef.current.offsetWidth
    const terminalsWidth = terminalsRef.current.scrollWidth
    const minInputWidth = containerWidth * 0.1
    const availableForTerminals = containerWidth - minInputWidth
    if (terminalsWidth > availableForTerminals) {
      terminalsRef.current.scrollLeft = terminalsWidth - availableForTerminals
    } else {
      terminalsRef.current.scrollLeft = 0
    }
  }

  useEffect(() => {
    window.addEventListener("resize", calculateOffset)
    return () => window.removeEventListener("resize", calculateOffset)
  }, [])

  useEffect(() => {
    if (!terminals?.length) {
      if (terminalsRef.current) terminalsRef.current.scrollLeft = 0
      return
    }
    const id = setTimeout(calculateOffset, 310)
    return () => clearTimeout(id)
  }, [terminals])

  const handleChange = (e) => {
    const value = e.target.value
    setQuery(value)
    onQueryChange?.(value)
  }

  const placeholder = isDeparture ? t("admin.city_of_departure") : t("admin.city_of_arrival")

  // ─── Шапка (общая для desktop и mobile) ──────────────────────────────────
  const Header = (
    <div className="sticky top-0 bg-white z-10 border-b border-gray-100 px-3 py-2">
      <div ref={containerRef} className="flex items-center w-full min-w-0 h-[37px] gap-2">
        <div
          ref={terminalsRef}
          className="overflow-hidden flex items-center min-w-0 max-w-[80%] flex-shrink-0"
          style={{ scrollBehavior: "smooth" }}
        >
          <div className="flex items-center gap-1 whitespace-nowrap">
            <AnimatePresence mode="popLayout" onExitComplete={calculateOffset}>
              {terminals.map((item) => (
                <motion.div
                  key={`${item.type}-${item.id}`}
                  layout
                  initial={{ opacity: 0, x: -20, scale: 0.9 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 20, scale: 0.9 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  <CityInput
                    value={item.city ?? item.name}
                    onClear={() => onClearTerminals?.(activeField)}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>

        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          placeholder={placeholder}
          className="flex-1 min-w-[10%] pl-2 text-sm outline-none"
        />

        {/* Кнопка закрытия — только на мобиле */}
        {isMobile && (
          <button
            onClick={onClose}
            className="flex-shrink-0 ml-1 p-1 rounded-full hover:bg-gray-100 transition"
          >
            <X className="w-4 h-4 text-app-content-subtle" />
          </button>
        )}
      </div>
    </div>
  )

  const Content = (
    <div className="p-2 overflow-y-auto flex-1">
      {isLoading ? (
        <div className="text-sm text-gray-400 text-center py-6">
          {t("common.loading", "Загрузка...")}
        </div>
      ) : destinations.length === 0 ? (
        <div className="text-sm text-gray-400 text-center py-6">
          {t("common.not_found", "Ничего не найдено")}
        </div>
      ) : (
        destinations.map((group) => (
          <SearchSuggestionsSection
            key={group.type}
            group={group}
            selectedFrom={selectedFrom}
            selectedTo={selectedTo}
            onSelect={(item) => onSelect?.(item)}
          />
        ))
      )}
    </div>
  )

  // ─── Mobile: fullscreen bottom sheet ─────────────────────────────────────
  if (isMobile) {
    return (
      <AnimatePresence>
        {/* Оверлей */}
        <motion.div
          key="overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-40 bg-black/40"
          onClick={onClose}
        />

        {/* Bottom sheet */}
        <motion.div
          key="sheet"
          ref={ref}
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 30, stiffness: 300 }}
          className={cn(
            "fixed bottom-0 left-0 right-0 z-50",
            "bg-white rounded-t-2xl shadow-2xl",
            "flex flex-col",
            "max-h-[90dvh]"
          )}
        >
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
            <div className="w-10 h-1 rounded-full bg-gray-200" />
          </div>

          {/* Заголовок */}
          <div className="px-4 pb-2 flex-shrink-0">
            <p className="text-base font-medium text-app-content">
              {isDeparture
                ? t("admin.city_of_departure", "Откуда")
                : t("admin.city_of_arrival", "Куда")}
            </p>
          </div>

          {Header}
          {Content}

          {/* Safe area для iOS */}
          <div className="flex-shrink-0 pb-safe h-4" />
        </motion.div>
      </AnimatePresence>
    )
  }

  // ─── Desktop: dropdown ────────────────────────────────────────────────────
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scaleY: 0 }}
      animate={{ opacity: 1, scaleY: 1 }}
      exit={{ opacity: 0, scaleY: 0 }}
      transition={{ duration: 0.2, ease: "easeInOut" }}
      style={{ transformOrigin: "top" }}
      className={cn(
        "absolute z-50 bg-white shadow-2xl overflow-hidden",
        "-top-[1px] w-full min-w-[400px] max-w-sm border border-gray-100",
        isDeparture ? "left-0 rounded-l-md" : "right-0 rounded-r-md",
        "flex flex-col max-h-80"
      )}
    >
      {Header}
      {Content}
    </motion.div>
  )
}

export default SearchSuggestionsModal
